import React, { forwardRef } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  unit?: string;
  stepper?: boolean;
  onStepUp?: () => void;
  onStepDown?: () => void;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      unit,
      stepper = false,
      onStepUp,
      onStepDown,
      className = '',
      containerClassName = '',
      disabled,
      required,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || (label ? `input-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

    return (
      <div className={`w-full space-y-1.5 ${containerClassName}`}>
        {/* Label & Required Indicator */}
        {label && (
          <div className="flex items-center justify-between">
            <label
              htmlFor={inputId}
              className="font-serif text-xs font-semibold uppercase tracking-wider text-ink-700 select-none flex items-center gap-1"
            >
              <span>{label}</span>
              {required && <span className="text-red-700">*</span>}
            </label>
            {unit && (
              <span className="font-mono text-[11px] text-ink-500 tracking-tight">
                {unit}
              </span>
            )}
          </div>
        )}

        {/* Input Wrapper */}
        <div className="relative flex items-center">
          <input
            id={inputId}
            ref={ref}
            disabled={disabled}
            required={required}
            inputMode={props.inputMode || (props.type === 'number' ? 'numeric' : undefined)}
            pattern={props.pattern || (props.type === 'number' ? '[0-9]*' : undefined)}
            className={`
              w-full bg-paper-100 border rounded-xs px-3 py-2 text-ink-900 font-mono text-sm
              transition-colors duration-150 outline-none
              placeholder:text-ink-400 placeholder:font-sans placeholder:text-xs
              focus:bg-white focus:border-ink-900 focus:ring-1 focus:ring-ink-900
              disabled:bg-paper-200 disabled:text-ink-400 disabled:cursor-not-allowed
              ${error ? 'border-red-600 bg-red-50/30 focus:border-red-700 focus:ring-red-700' : 'border-paper-400'}
              ${unit && !stepper ? 'pr-12' : ''}
              ${stepper ? 'pr-16' : ''}
              ${className}
            `}
            {...props}
          />

          {/* Unit tag inside input */}
          {unit && !stepper && (
            <div className="absolute right-3 pointer-events-none text-xs font-mono text-ink-500">
              {unit}
            </div>
          )}

          {/* Stepper Buttons for Touch / Rapid Numeric Entry */}
          {stepper && (
            <div className="absolute right-1 flex items-center gap-1">
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={onStepDown}
                className="w-6 h-7 flex items-center justify-center bg-paper-200 hover:bg-paper-300 active:bg-paper-400 border border-paper-400 text-ink-900 rounded-xs text-xs font-bold select-none disabled:opacity-40"
                aria-label="Decrease value"
              >
                -
              </button>
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={onStepUp}
                className="w-6 h-7 flex items-center justify-center bg-paper-200 hover:bg-paper-300 active:bg-paper-400 border border-paper-400 text-ink-900 rounded-xs text-xs font-bold select-none disabled:opacity-40"
                aria-label="Increase value"
              >
                +
              </button>
            </div>
          )}
        </div>

        {/* Error Message or Helper Text */}
        {error ? (
          <p className="font-serif text-xs text-red-700 flex items-center gap-1 mt-1">
            <span>⚠</span>
            <span>{error}</span>
          </p>
        ) : helperText ? (
          <p className="font-serif text-[11px] text-ink-500 mt-1">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
